import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { Component, ErrorMessages } from "utils/constants";
import { useStyles } from "./dropdownlist.style";
import { t } from "i18next";
import { InfoTooltip } from "components";

interface DropDownListProps {
  id: string;
  label: string;
  labelId: string;
  svgLabelIcon?: React.ReactNode | null;
  tooltipContent?: string;
  tooltipAriaLabel?: string;
  data: any;
  dropdownTextField?: string | null;
  dropdownKey?: string | null;
  className?: string;
  rootClass?: string;
  labelClassName?: string;
  selectedValue: unknown;
  defaultItem: unknown;
  ariaLabelledBy?: string;
  popupSettings?: unknown;
  required: boolean;
  error?: boolean | null;
  errorMessage?: string | null;
  onChange: (value: string) => void;
  asteriskClassName?: string;
  disabled?: boolean;
}

export const DropdownList: React.FC<DropDownListProps> = ({
  id,
  label,
  svgLabelIcon,
  labelId,
  tooltipContent,
  tooltipAriaLabel,
  data,
  dropdownTextField,
  dropdownKey,
  rootClass,
  className,
  labelClassName,
  ariaLabelledBy,
  popupSettings,
  error,
  errorMessage,
  required,
  onChange,
  defaultItem,
  selectedValue,
  asteriskClassName,
  disabled = false,
}: DropDownListProps): React.ReactElement => {
  const classes = useStyles();
  useEffect(() => {
    if (data) {
      const dropdownList = document.querySelectorAll(".k-dropdown-wrap");
      for (let index = 0; index < dropdownList.length; index++) {
        dropdownList[index].setAttribute("role", "combobox");
        dropdownList[index].setAttribute("aria-haspopup", "listbox");
      }
    }
  }, [data]);

  // Smart comparison: handles both object and string defaultItem
  const isDefaultSelected =
    typeof defaultItem === "object"
      ? JSON.stringify(selectedValue) === JSON.stringify(defaultItem)
      : selectedValue === defaultItem;

  return (
    <Box className={rootClass}>
      <Box className={classes.labelWithTooltip}>
        <Typography className={labelClassName}>
          {svgLabelIcon}
          <label id={labelId} htmlFor={id}>
            {label} {required && <span className={asteriskClassName}>*</span>}
          </label>
        </Typography>
        {tooltipContent && (
          <InfoTooltip
            id={`${id}-tooltip`}
            tipText={tooltipContent}
            ariaLabel={tooltipAriaLabel || "More information"}
          />
        )}
      </Box>
      <span id={`${id}-value`} className="sr-only">
        {typeof selectedValue === "object" && selectedValue !== null
          ? (selectedValue as any).title
          : selectedValue}
      </span>
      <DropDownList
        tabIndex={0}
        id={id}
        disabled={disabled}
        data={data}
        required={required}
        ariaLabelledBy={`${labelId ?? ariaLabelledBy} ${id}-value`}
        ariaDescribedBy={`${Component.ErrorMessageId}${id}`}
        popupSettings={popupSettings}
        defaultItem={defaultItem}
        textField={dropdownTextField}
        dataItemKey={dropdownKey}
        className={className}
        onChange={(event: DropDownListChangeEvent) => onChange(event.target.value)}
        value={selectedValue}
        onOpen={() => {
          setTimeout(() => {
            const ulListElement = document.querySelector(`#${id}`).getAttribute("aria-owns");
            document.getElementById(`${ulListElement}`).setAttribute("aria-labelledby", labelId);
          }, 0);
        }}
      />
      {((required && (!selectedValue || isDefaultSelected)) || error) && (
        <div role="alert">
          <span id={`${Component.ErrorMessageId}${id}`} className={classes.errorText}>
            {errorMessage ||
              (label
                ? `${label.replace(Component.Star, "")} ${ErrorMessages.InputRequired}`
                : ErrorMessages.InputRequired)}
          </span>
        </div>
      )}
    </Box>
  );
};
